import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Chip,
  Grid,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useState } from "react";
import { SubscriptionType } from "schemas/dashboard";
import { Map } from "schemas/functions";
import { Plans, SubscriptionMessages } from "schemas/payments";
import theme from "styles/theme";

import { VA_MONEY_BACK_DEAL } from "constants/influencer";
import { SubscriptionColor } from "utils/color";
import { formatEpochTimestamp } from "utils/time";
import { trackEvent } from "utils/tracking";

import CancelDialog from "./CancelDialog";
import ManageDialog from "./ManagePlanDialog";
import {
  disabledCurrentPlan,
  getCurrentPlanButton,
  getOtherPlanButton,
  getStatusChipLabel,
  getSubscriptionInformation,
} from "./helpers";
import styles from "./styles";

interface IProps {
  plan: Plans;
  loading: boolean;
  handleCheckout: (
    plan: Plans,
    flow?: string,
    openTarget?: "_self" | "_blank",
  ) => void;
  renewSubscription: (plan: Plans) => void;
  allLoading?: Map;
  fromOnboarding?: boolean;
  notInterestedButton?: JSX.Element;
}

export function Plan({
  loading,
  handleCheckout,
  renewSubscription,
  plan,
  allLoading,
  fromOnboarding,
  notInterestedButton,
}: IProps) {
  const {
    subscription,
    plans,
    emailSents,
    brandRequestsMade,
    customizedMessagesDrafted,
    recommendationsRefreshed,
    upgradeDialogSource,
    tab,
    isStarterPlan,
  } = useContext(SubscriptionContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { discoverTab } = useContext(DiscoverViewContext);
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobileScreen =
    isMediumScreen || (isLargeScreen && discoverTab !== null);
  const isXLScreen = useMediaQuery("(min-width: 1600px)");

  const isStarterOnProPlan =
    isStarterPlan && (plan === Plans.PRO || plan === Plans.PRO_YEARLY);

  const { stripePlan, isCurrentPlan, price, description, resubscribe, renew } =
    getSubscriptionInformation(plan, plans, subscription, currentOrg, {
      emailSents,
      brandRequestsMade,
      customizedMessagesDrafted,
      recommendationsRefreshed,
      tab,
    });

  const [cancelDialog, setCancelDialog] = useState<boolean>(false);
  const [manageDialog, setManageDialog] = useState<boolean>(false);

  const backgroundColor = stripePlan?.status
    ? SubscriptionColor[stripePlan?.status]
    : "";

  const openCancel = () => {
    trackEvent(
      "Manage Subscription Dialog Cancel Subscription Button Pressed",
      {
        plan,
      },
    );
    setManageDialog(false);
    setCancelDialog(true);
  };

  const closeManageDialog = () => {
    trackEvent("Manage Subscription Dialog Closed", {
      plan,
    });
    setManageDialog(false);
  };

  const closeCancelDialog = () => {
    trackEvent("Cancel Subscription Dialog Closed", {
      plan,
    });
    setCancelDialog(false);
  };

  const handleModifyCurrentPlan = () => {
    if (resubscribe) {
      trackEvent("Resubscribe Subscription Button Pressed", {
        plan,
      });
      handleCheckout(plan);
    } else if (renew) {
      trackEvent("Renew Subscription Button Pressed", {
        plan,
      });
      renewSubscription(plan);
    } else {
      trackEvent("Manage Plan Button Pressed", {
        plan,
      });
      setManageDialog(true);
    }
  };

  const handleUpgradeTonNewPlan = () => {
    if (fromOnboarding) {
      trackEvent("VA Onboarding Flow - Pay Now Button Pressed", { plan });
    } else {
      trackEvent(`Switch Plan Button Pressed`, {
        plan,
      });
    }
    handleCheckout(plan, "subscription_update");
  };

  const renderPrice = () => {
    const currentPlanPrice = <>${price.toFixed(2)}/month</>;

    if (price === 0) {
      return "$0";
    } else if (plan === Plans.PRO_YEARLY) {
      return (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box sx={styles.strike}>
            <Box sx={{ color: "black" }}>
              ${plans && plans[Plans.PRO]?.price}/month
            </Box>
          </Box>
          {currentPlanPrice}
        </Box>
      );
    } else if (fromOnboarding && plan === Plans.VA_YEARLY) {
      return <Box sx={{ color: "black" }}>${VA_MONEY_BACK_DEAL}/year</Box>;
    } else {
      return currentPlanPrice;
    }
  };

  const getGrid = () => {
    if (upgradeDialogSource === "Exclusive Contact") {
      return 6;
    } else if (fromOnboarding) return 8;
    if (isMobileScreen) return 12;
    if (isXLScreen) return 4;
    return 6;
  };

  const planButtonText = getOtherPlanButton(
    plan,
    subscription?.subscription?.planName,
    currentOrg,
    fromOnboarding,
  );

  const canceledVA =
    stripePlan?.planName === Plans.VA &&
    (stripePlan.status === SubscriptionType.CANCELED || stripePlan.cancelAt);

  return (
    <Grid
      item
      xs={12}
      md={getGrid()}
      container
      direction="column"
      sx={fromOnboarding ? styles.onboarding : { p: 3 }}
    >
      <Grid item container xs="auto" sx={{ mb: 3 }}>
        <Grid item xs="auto" sx={{ mr: 1 }}>
          <Typography variant="h5">
            {plans && plans[plan].display_name}
            {isCurrentPlan && (
              <Chip
                size="small"
                sx={[styles.chip, { backgroundColor }]}
                label={getStatusChipLabel(subscription)}
              />
            )}
            <br />
            {renderPrice()}
          </Typography>

          {plan === Plans.VA_YEARLY && (
            <Box sx={styles.moneyBack}>
              💸{" "}
              <em>
                money back guaranteed if you subscribe by April 7th, 11:59pm EST
              </em>
            </Box>
          )}
        </Grid>
      </Grid>
      <Grid item xs sx={styles.textContainer}>
        <Typography sx={styles.planDescription}>{description}</Typography>
        {isCurrentPlan && (
          <>
            {subscription && subscription?.remainingMessages <= 0 ? (
              <Typography paragraph sx={styles.warningText}>
                Please upgrade to send more this month
              </Typography>
            ) : (
              <Typography paragraph sx={styles.warningText}>
                <i>
                  {stripePlan?.status &&
                    stripePlan?.status in SubscriptionMessages &&
                    SubscriptionMessages[stripePlan?.status]}
                  {stripePlan?.cancelAt &&
                    ` Your subscription is set to cancel on ${formatEpochTimestamp(
                      {
                        epochTimestamp: stripePlan.cancelAt,
                        addYear: true,
                      },
                    )}. Currently, all follow-ups and brand requests beyond your
                            cancellation date will be deleted.`}
                  {canceledVA && (
                    <>
                      <br />
                      <br />
                      We do not offer this plan anymore, please choose another
                      plan to subscribe to.
                    </>
                  )}
                </i>
              </Typography>
            )}
          </>
        )}

        {isStarterOnProPlan && (
          <Alert severity="info" sx={{ my: 2 }}>
            We have moved you from <strong>Starter tier</strong> to Pro tier
            because we no longer offer the Starter tier on our platform. You
            will still be charged the same rate as the Starter tier until you{" "}
            <strong>change or cancel</strong> your plan.
          </Alert>
        )}
      </Grid>
      <Grid item xs="auto">
        {((plan === Plans.VA && !canceledVA) || plan !== Plans.VA) && (
          <>
            {isCurrentPlan ? (
              <LoadingButton
                loading={resubscribe && loading}
                color="primary"
                variant={resubscribe || renew ? "contained" : "outlined"}
                fullWidth={!fromOnboarding}
                disabled={disabledCurrentPlan(plan, subscription)}
                onClick={handleModifyCurrentPlan}
                disableElevation
              >
                {getCurrentPlanButton(plan, subscription)}
              </LoadingButton>
            ) : (
              <Grid container alignItems="center">
                {plan !== Plans.FREE && !subscription?.overridePlanName && (
                  <LoadingButton
                    loading={loading}
                    onClick={handleUpgradeTonNewPlan}
                    color={
                      planButtonText === "Choose This Plan" && !canceledVA
                        ? "secondary"
                        : "primary"
                    }
                    variant={
                      planButtonText === "Choose This Plan" && !canceledVA
                        ? "outlined"
                        : "contained"
                    }
                    fullWidth={!fromOnboarding}
                    disableElevation
                  >
                    {planButtonText}
                  </LoadingButton>
                )}
                {fromOnboarding && notInterestedButton}
              </Grid>
            )}
          </>
        )}

        <ManageDialog
          loading={loading}
          open={manageDialog}
          openCancel={openCancel}
          onClose={closeManageDialog}
          handleCheckout={handleCheckout}
          plan={plan}
          allLoading={allLoading}
        />

        <CancelDialog
          loading={loading}
          open={cancelDialog}
          onClose={closeCancelDialog}
          handleCheckout={handleCheckout}
          plan={plan}
        />
      </Grid>
    </Grid>
  );
}
